import memoizeOne from 'memoize-one'

import { LocaleToCurrency, ShortDistanceUnits } from '@/travelsuit'
import { Money } from '@/types/billing'
import { Currency, UserCurrency } from '@/types/common'
import { User } from '@/types/user'

import { storages } from './browserStorage'
import { TFunction } from './i18n/i18n'
import { getCurrentLocale } from './i18n/locale-utils'

interface NumberTransformConfig {
	precision: number
	removeZeroes: boolean
	locale: string
}

interface CurrencyConfig {
	currency: Currency
}

type GetCurrencyFormatterConfig = Partial<NumberTransformConfig & CurrencyConfig>
type GetNumberFormatterConfig = Partial<
	NumberTransformConfig & Omit<Intl.NumberFormatOptions, 'maximumFractionPoints' | 'minimumFractionPoints'>
>

export function getNumberFormatter({ precision = 2, locale, ...formatterConfig }: GetNumberFormatterConfig = {}) {
	return new Intl.NumberFormat(locale ?? getCurrentLocale(), {
		...formatterConfig,
		maximumFractionDigits: precision,
		minimumFractionDigits: precision,
	})
}

function getCurrencyFormatter({
	precision,
	currency: curr = UserCurrency.USD,
}: GetCurrencyFormatterConfig = {}): Intl.NumberFormat {
	return getNumberFormatter({
		precision,
		style: 'currency',
		currency: curr.toString(),
	})
}

function removeZerosFromText(text: string, precision?: number) {
	const decSep =
		getNumberFormatter({ precision })
			.formatToParts(1.1)
			.find((part) => part.type === 'decimal')?.value ?? '.'
	return text.replace(new RegExp(`\\${decSep}0+$`), '')
}

export function getUserCurrency(): UserCurrency {
	const user: User = JSON.parse(storages.local.getItem('internal_user') ?? '{}')
	return user.currency_code ?? LocaleToCurrency[getCurrentLocale()]
}

export function commatize(
	num: string | number,
	{ precision = 2, removeZeroes = true, ...rest }: Partial<NumberTransformConfig> = {},
): string {
	let formatted = getNumberFormatter({ precision, ...rest }).format(Number(num))
	if (removeZeroes) {
		formatted = removeZerosFromText(formatted)
	}
	return formatted
}

export function getCurrency(curr = getUserCurrency(), showCurrencyName?: boolean) {
	const currencySign = getCurrencySign(curr)
	return showCurrencyName ? curr.toString() + ' (' + currencySign + ')' : currencySign
}

export function getCurrencySign(currency: Currency) {
	return new Intl.NumberFormat(getCurrentLocale(), { style: 'currency', currency })
		.formatToParts()
		.find((part) => part.type === 'currency')!.value
}

export function formatCurrency(
	num: number | string,
	{ removeZeroes, currency = getUserCurrency(), ...rest }: Partial<NumberTransformConfig & CurrencyConfig> = {},
) {
	const actualNumber: number = typeof num === 'number' ? num : Number(num)
	let formattedNumber = getCurrencyFormatter({ currency, ...rest }).format(actualNumber)
	if (removeZeroes) {
		formattedNumber = removeZerosFromText(formattedNumber)
	}

	return formattedNumber
}

interface DistanceResult {
	distance: string
	amount: number
	key: string
}

function distance(
	dist: number,
	keySuffix: 'short' | 'long' = 'short',
	unit: ShortDistanceUnits = ShortDistanceUnits.KM,
): DistanceResult {
	const isMI = [ShortDistanceUnits.MI].includes(unit)
	const isK = dist > 1

	return {
		distance: commatize(isMI || isK ? dist : dist * 1000, { removeZeroes: true }),
		amount: isMI || isK ? dist : dist * 1000,
		key: `units.${isMI ? 'miles' : isK ? 'kilometers' : 'meters'}-${keySuffix}`,
	}
}

const getDistanceUnitsTranslations = (t: TFunction, { amount, count }: { amount: string; count: number }) => ({
	'units.kilometers-long': t('units.kilometers-long', { count, amount }),
	'units.kilometers-short': t('units.kilometers-short', { count, amount }),
	'units.meters-long': t('units.meters-long', { count, amount }),
	'units.meters-short': t('units.meters-short', { count, amount }),
	'units.miles-long': t('units.miles-long', { count, amount }),
	'units.miles-short': t('units.miles-short', { count, amount }),
})

export function distanceT(
	t: TFunction,
	dist: number,
	keySuffix: 'short' | 'long' = 'short',
	unit: ShortDistanceUnits = ShortDistanceUnits.KM,
) {
	const res = distance(dist, keySuffix, unit)

	// @ts-expect-error todo if you see this please remove this comment and fix the type error
	return getDistanceUnitsTranslations(t, { amount: res.distance, count: res.amount })[res.key]
}

export function convertKmToMi(km: number): number {
	return km / 1.609
}

function isMoney(obj: React.ReactNode | Money): obj is Money {
	return !!obj && obj.hasOwnProperty('amount') && obj.hasOwnProperty('currency')
}

export function moneyAsString(obj: React.ReactNode | Money): React.ReactNode {
	if (isMoney(obj)) {
		return formatCurrency(obj.amount, { currency: obj.currency })
	}

	if (typeof obj === 'number') {
		return formatCurrency(obj)
	}

	return obj
}

export const getFormatOrdinals = memoizeOne((locale: string) => {
	const pr = new Intl.PluralRules(locale, { type: 'ordinal' })

	const lang = locale.slice(0, 2)

	const suffixes =
		{
			en: new Map([
				['one', 'st'],
				['two', 'nd'],
				['few', 'rd'],
				['other', 'th'],
			]),
		}[lang] || new Map([['other', '.']])

	return (n: number) => {
		const rule = pr.select(n)
		const suffix = suffixes.get(rule)
		return `${n}${suffix}`
	}
})
